// Result navigation shares the same ordering, filters and viewers as the result tables.
const labels = { context: '문맥', consistency: '일치성', omission: '누락사항', typo: '오탈자', copy: '카피', instruction: '지침', state_evaluation: '상태평가' };
const viewers = { context: 'viewContextResult', consistency: 'viewConsistencyResult', omission: 'viewOmissionResult', typo: 'viewTypoResult', copy: 'viewCopyResult', instruction: 'openInstructionDetails', state_evaluation: 'viewStateEvaluationComparison' };

function kindOf(room, row, fallback) {
    const type = room.canonicalResultType(row);
    return ['text_copy', 'image_copy'].includes(type) ? 'copy' : (labels[type] ? type : fallback);
}

export function navigationRows(room, kind) {
    let rows = [];
    if (kind === 'context') rows = room.contextFindingGroups.flatMap(group => group.items);
    else if (kind === 'consistency') rows = room.consistencyFindingGroups.flatMap(group => group.items);
    else if (kind === 'omission') rows = ['basic', 'detail'].flatMap(sheet => room.getRenderedOmissionDisplayRowsBySheetType(sheet).filter(row => row.kind === 'item').map(row => row.item));
    else if (kind === 'typo') rows = room.renderedTypoFindings;
    else if (kind === 'copy') rows = room.renderedCopyFindings;
    else if (kind === 'instruction') rows = room.renderedInstructionReviewFindings;
    else if (kind === 'state_evaluation') rows = room.renderedStateEvaluationFindings;
    return (rows || []).filter(row => kind === 'instruction' || (kind === 'omission' ? room.canViewOmissionResult(row) : room.canViewResultTarget(row) || room.canViewResultReference(row)));
}

function sequence(room, row, kind) {
    const rows = navigationRows(room, kind).slice();
    let index = rows.indexOf(row);
    if (index < 0) index = rows.findIndex(candidate => room.resultIdentity(candidate) === room.resultIdentity(row));
    // A notice or a result removed by a live update remains individually viewable.
    if (index < 0) return { rows: [row], index: 0, kind, label: labels[kind] || '검사 결과' };
    return { rows, index, kind, label: labels[kind] || '검사 결과' };
}

function heading(row) {
    if (row?.original && row?.corrected) return `${row.original} → ${row.corrected}`;
    return row?.item || row?.title || row?.description || '검사 결과';
}

export function installFindingNavigation(Room) {
    const proto = Room.prototype;
    const originals = {};
    const entryPoints = {
        viewContextResult: 'context', viewConsistencyResult: 'consistency',
        viewOmissionResult: 'omission', viewTypoResult: 'typo',
        viewCopyResult: 'copy', viewCopyResultRef: 'copy',
        openInstructionDetails: 'instruction',
        viewStateEvaluationReport: 'state_evaluation',
        viewStateEvaluationGuideline: 'state_evaluation',
        viewStateEvaluationComparison: 'state_evaluation',
        viewResultDetail: null, viewRefPdf: null
    };

    async function open(room, original, args, kind, existing) {
        if (room.findingNavigationBusy) return;
        const nav = existing || sequence(room, args[0], kind);
        if (!existing) nav.trigger = typeof document === 'undefined' ? null : document.activeElement;
        room.findingNavigation = nav;
        room.findingNavigationBusy = true;
        room.findingNavigationError = '';
        room.instructionModalRequest++;
        room.documentViewerModalOpen = true;
        room.activeViewerHighlight = null;
        room.compareEvidenceDetail = null;
        try {
            await room.service.render();
            if (room.findingNavigation !== nav) return;
            let pending;
            // Nested viewer helpers must not create a second navigation sequence.
            room._findingDispatching = true;
            try { pending = original.apply(room, args); }
            finally { room._findingDispatching = false; }
            await pending;
        } catch (error) {
            if (room.findingNavigation === nav) {
                room.pdfUrl = '';
                room.pdfImageUrl = '';
                room.compareMode = false;
                room.compareLeft = room.compareRight = null;
                room.findingNavigationError = '근거를 불러오지 못했습니다. 다른 항목으로 이동하거나 다시 열어 주세요.';
            }
            console.warn('Failed to open finding evidence:', error);
        } finally {
            if (room.findingNavigation === nav) {
                room.findingNavigationBusy = false;
                await room.service.render();
            }
        }
    }

    for (const [method, fallback] of Object.entries(entryPoints)) {
        const original = proto[method];
        if (typeof original !== 'function') continue;
        originals[method] = original;
        proto[method] = function (...args) {
            if (this._findingDispatching || !args[0]) return original.apply(this, args);
            const kind = kindOf(this, args[0], fallback);
            if (!kind) return original.apply(this, args);
            return open(this, original, args, kind);
        };
    }

    proto.findingNavigationTitle = function () {
        const nav = this.findingNavigation;
        return nav ? heading(nav.rows[nav.index]) : '';
    };
    proto.moveFinding = async function (delta, event) {
        event?.preventDefault();
        event?.stopPropagation();
        const nav = this.findingNavigation;
        if (!nav || !this.documentViewerModalOpen || this.findingNavigationBusy) return;
        const next = nav.index + delta;
        if (!Number.isInteger(next) || next < 0 || next >= nav.rows.length) return;
        const row = nav.rows[next];
        let method = viewers[nav.kind];
        if (nav.kind === 'state_evaluation' && !this.canCompareStateEvaluationEvidence(row)) {
            method = this.canViewStateEvaluationReport(row) ? 'viewStateEvaluationReport' : 'viewStateEvaluationGuideline';
        }
        nav.index = next;
        await open(this, originals[method], [row], nav.kind, nav);
        if (this.findingNavigation === nav && typeof document !== 'undefined') {
            const modal = document.querySelector('.document-viewer-panel.is-modal');
            modal?.querySelectorAll('.document-image-viewer, .instruction-detail-panel, .document-viewer-stage').forEach(element => { element.scrollTop = 0; });
        }
    };

    const close = proto.closeDocumentViewerModal;
    proto.closeDocumentViewerModal = async function (...args) {
        const nav = this.findingNavigation;
        this.findingNavigation = null;
        this.findingNavigationBusy = false;
        this.findingNavigationError = '';
        await close.apply(this, args);
        if (nav?.trigger?.isConnected) nav.trigger.focus();
    };
}
